/**
 * Local mock data for offline dev / API-down (CLAUDE.md: "Mock data module for local dev").
 * Activated when VITE_USE_MOCKS=1 or when a network request fails and mocks are allowed.
 *
 * Russian copy follows the spec framing (TZ §9): помогаем сотруднику, объективные KPI —
 * без карательной формулировки.
 */
import type { AnomalyCard, ClipLink, KpiValue } from "@/types/contract";
import { NotFoundError } from "@/api/errors";

const now = Date.now();
const iso = (msAgo: number): string => new Date(now - msAgo).toISOString();

export const mockAnomalies: AnomalyCard[] = [
  {
    id: "an-1001",
    restaurant_id: "demo-venue",
    title: "Стол 12 ждёт официанта 8 минут",
    category: "service",
    priority: 4,
    money_impact: null,
    event_id: "ev-7a1",
    clip_ref: "clip-7a1",
    linked_data: {
      table_no: 12,
      wait_seconds: 480,
      zone: "Зал · юг",
      shift_staff: "Смена А · официант №3",
    },
    status: "open",
    created_at: iso(2 * 60 * 1000),
  },
  {
    id: "an-1002",
    restaurant_id: "demo-venue",
    title: "Касса 2: отмена чека без продажи на ₸3 400",
    category: "loss_prevention",
    priority: 5,
    money_impact: 3400,
    event_id: "ev-7a2",
    clip_ref: "clip-7a2",
    linked_data: {
      pos_exception_type: "void",
      amount: 3400,
      receipt_no: "Ч-2026-04412",
      checkout_id: "cash-2",
      shift_staff: "Смена А · кассир №1",
    },
    status: "open",
    created_at: iso(6 * 60 * 1000),
  },
  {
    id: "an-1003",
    restaurant_id: "demo-venue",
    title: "Кухня: вок +40% к норме за последний час",
    category: "kitchen",
    priority: 3,
    money_impact: null,
    event_id: "ev-7a3",
    clip_ref: null,
    linked_data: {
      station: "Вок",
      deviation_pct: 40,
      orders_in_hour: 28,
      norm_per_hour: 20,
    },
    status: "open",
    created_at: iso(14 * 60 * 1000),
  },
  {
    id: "an-1004",
    restaurant_id: "demo-venue",
    title: "Приёмка: недовоз по накладной №INV-2026-0098 (22 из 24)",
    category: "receiving",
    priority: 4,
    money_impact: 5600,
    event_id: "ev-7a4",
    clip_ref: "clip-7a4",
    linked_data: {
      invoice_no: "INV-2026-0098",
      expected_units: 24,
      detected_units: 22,
      supplier: "ТОО «Свежесть»",
    },
    status: "acknowledged",
    created_at: iso(40 * 60 * 1000),
  },
  {
    id: "an-1005",
    restaurant_id: "demo-venue",
    title: "Очередь на входе: 7 гостей, есть уход без посадки",
    category: "flow",
    priority: 2,
    money_impact: null,
    event_id: "ev-7a5",
    clip_ref: "clip-7a5",
    linked_data: { queue_len: 7, balk_count: 2, zone: "Вход" },
    status: "open",
    created_at: iso(22 * 60 * 1000),
  },
  {
    id: "an-1006",
    restaurant_id: "demo-venue",
    title: "Гигиена: вход на кухню без обработки рук",
    category: "hygiene",
    priority: 3,
    money_impact: null,
    event_id: "ev-7a6",
    clip_ref: "clip-7a6",
    linked_data: { zone: "Вход на кухню", needs_review: true },
    status: "resolved",
    created_at: iso(90 * 60 * 1000),
  },
];

export const mockKpis: KpiValue[] = [
  {
    metric: "median_time_to_guest",
    value: 142,
    unit: "seconds",
    target: 120,
    period_from: new Date(now - 86_400_000).toISOString().slice(0, 10),
    period_to: new Date(now).toISOString().slice(0, 10),
  },
  {
    metric: "table_turnover",
    value: 1.8,
    unit: "covers/hour",
    target: 2.0,
    period_from: new Date(now - 86_400_000).toISOString().slice(0, 10),
    period_to: new Date(now).toISOString().slice(0, 10),
  },
  {
    metric: "foodcost_dev",
    value: 2.4,
    unit: "%",
    target: 0,
    period_from: new Date(now - 86_400_000).toISOString().slice(0, 10),
    period_to: new Date(now).toISOString().slice(0, 10),
  },
];

export function mockClip(anomalyId: string): ClipLink {
  const hasClip = mockAnomalies.find((a) => a.id === anomalyId)?.clip_ref;
  if (!hasClip) {
    // Simulate a TTL-expired / missing clip.
    throw new NotFoundError("Клип недоступен или удалён по TTL");
  }
  return {
    // A short freely-usable sample so the player has something to show in dev.
    uri: "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
    ttl_expires_at: new Date(now + 30 * 60 * 1000).toISOString(),
  };
}

export const mockTables: import("@/types/contract").TableStatus[] = [
  { code: "12", department: "Зал", status: "occupied", total: 8700, opened_min: 40 },
  { code: "5", department: "Зал", status: "free", total: null, opened_min: null },
  { code: "888", department: "Зал", status: "attention", total: 750904, opened_min: 51817 },
];

export const mockRestaurants: import("@/types/contract").Restaurant[] = [
  { id: "demo-venue", name: "Демо-точка" },
];

/** Смена: столы × заказы — живой пример с расхождением для дев-режима. */
export function mockTimeline(): import("@/types/contract").ShiftTimeline {
  const now = Date.now();
  const iso = (minAgo: number) =>
    new Date(now - minAgo * 60_000).toISOString().replace(/\.\d{3}Z$/, "Z");
  return {
    restaurant_id: "demo-venue",
    from: iso(240),
    to: iso(0),
    shift: { start_date: iso(240), open_shifts: 1 },
    lanes: [
      {
        zone_id: "table-21",
        camera_id: "cam-ch14",
        nvr_channel: 14,
        pos_table_code: "21",
        mapping_status: "confirmed",
        occupancy: [
          { from: iso(180), to: iso(120) },
          { from: iso(45), to: iso(5) },
        ],
        seatings: [iso(181), iso(46)],
        orders: [
          {
            number: "6701",
            table_code: "21",
            opened_at: iso(175),
            closed_at: iso(125),
            status: "fiscalized",
            total: 12400,
            guest_count: 3,
          },
        ],
        occupied_no_order_min: 40,
        order_no_occupancy_min: 4,
      },
      {
        zone_id: "letka3-roww",
        camera_id: "cam-ch32",
        nvr_channel: 32,
        pos_table_code: null,
        mapping_status: "ambiguous",
        occupancy: [{ from: iso(90), to: iso(30) }],
        seatings: [iso(91)],
        orders: [],
        occupied_no_order_min: 60,
        order_no_occupancy_min: 0,
      },
    ],
    unmapped_orders: [
      {
        number: "6702",
        table_code: "75",
        opened_at: iso(35),
        closed_at: null,
        status: "open",
        total: 5600,
        guest_count: 2,
      },
    ],
  };
}
